import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';
import { propsFromMapEntry, preorderPropsFromDataset, applyPreorderInputs } from '@theme/lettoria-preorder-property-builder';
import { testedPrice, campaignPrice, money, repriceTested, onTestedPrices } from '@theme/lettoria-tested-price';

// PDP label behaviors that sit on top of the native product form:
// - <lettoria-coupon>: tap-to-copy ticket with the 1.6s "Copied ✓" revert
// - pair add-on toggle: flips the box, adds the companion via /cart/add.js
//   right before the native submit so the drawer opens showing both items
// - ATC label & sticky-bar price mirror the with-code amounts (recomputed on
//   every variant morph via a MutationObserver on the coupon box)
// Every figure is computed from the A/B tested price (lettoria-tested-price)
// — never parsed back out of the rendered text — so a challenger visitor sees
// the strip, button, sticky bar and add-on priced from THEIR catalog price.

class LettoriaCoupon extends HTMLElement {
  connectedCallback() {
    const ticket = this.querySelector('[ref-ticket]');
    const label = this.querySelector('[ref-copy-label]');
    ticket?.addEventListener('click', () => {
      const done = () => {
        label.textContent = 'Copied ✓';
        label.classList.add('is-copied');
        setTimeout(() => {
          label.textContent = 'Tap to copy';
          label.classList.remove('is-copied');
        }, 1600);
      };
      const code = ticket.getAttribute('data-code') ?? '';
      if (navigator.clipboard) navigator.clipboard.writeText(code).then(done, done);
      else done();
    });
  }
}
if (!customElements.get('lettoria-coupon')) customElements.define('lettoria-coupon', LettoriaCoupon);

function initLabel(section) {
  const addon = section.querySelector('[ref-pair-addon]');
  const form = section.querySelector('form[action*="/cart/add"], product-form-component form');

  addon?.addEventListener('click', () => {
    addon.classList.toggle('is-off');
    addon.setAttribute('aria-pressed', addon.classList.contains('is-off') ? 'false' : 'true');
    refreshTotals(section);
  });

  // add the companion first, then let the native flow submit the main item —
  // the drawer's section render then comes from the native add's response and
  // shows both lines. The submit event can't be intercepted: Horizon's
  // component framework delegates on:submit at document capture and registers
  // before this module, so its add always fires first. Instead the button
  // click is deferred (its default action — submitting the form — hasn't run
  // yet at click time), the companion is added, then the form submits once.
  const addCompanion = async (addon) => {
    try {
      const body = new FormData();
      body.append('items[0][id]', addon.getAttribute('data-variant-id'));
      body.append('items[0][quantity]', '1');
      // Armed companion: the pre-order properties ride along (app contract —
      // every /cart/add for an armed variant must carry them). The data-po-*
      // pair is rendered by lettoria-preorder-line-props on the addon button.
      const poProps = preorderPropsFromDataset(addon);
      if (poProps) {
        for (const [key, value] of Object.entries(poProps)) {
          body.append(`items[0][properties][${key}]`, value);
        }
      }
      await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
    } catch (error) {
      console.error('pair add-on add failed', error);
    }
  };

  if (form) {
    const submitButton = form.querySelector('button[type="submit"][name="add"]');

    // Pressed feedback the moment the buyer clicks: Horizon's checkmark burst
    // only fires after the network round-trip, so for up to a couple of
    // seconds the button looked dead. `is-adding` shows a spinner until the
    // native flow stamps data-added (or a timeout clears a failed add).
    submitButton?.addEventListener('click', () => {
      if (submitButton.disabled || submitButton.classList.contains('is-adding')) return;
      submitButton.classList.add('is-adding');
      const done = () => {
        submitButton.classList.remove('is-adding');
        observer.disconnect();
      };
      const observer = new MutationObserver(() => {
        if (submitButton.dataset.added) done();
      });
      observer.observe(submitButton, { attributes: true, attributeFilter: ['data-added'] });
      setTimeout(done, 6000);
    });

    submitButton?.addEventListener('click', async (event) => {
      const activeAddon = section.querySelector('[ref-pair-addon]:not(.is-off)');
      if (!activeAddon || form.dataset.pairHandled) return;
      event.preventDefault();
      // The companion round-trip runs before the native submit, so the drawer
      // would otherwise sit closed for both requests — open it at click time;
      // the cart-drawer [is-adding] spinner covers the empty-cart case.
      const drawerEl = document.getElementById('cart-drawer');
      const drawerComp = drawerEl?.querySelector('cart-drawer-component');
      if (drawerComp?.hasAttribute('auto-open') && !drawerEl.hasAttribute('open')) {
        if (drawerComp.querySelector('.cart-drawer--empty')) drawerComp.setAttribute('is-adding', '');
        drawerEl.open?.();
      }
      await addCompanion(activeAddon);
      form.dataset.pairHandled = 'true';
      form.requestSubmit();
      setTimeout(() => delete form.dataset.pairHandled, 500);
    });

    // Non-click submits (e.g. Enter in the quantity field) still get the
    // companion; here the native add is already in flight, so this only
    // guarantees cart contents — the drawer render may lag one update.
    form.addEventListener('submit', (event) => {
      if (event.defaultPrevented || form.dataset.pairHandled) return;
      const activeAddon = section.querySelector('[ref-pair-addon]:not(.is-off)');
      if (!activeAddon) return;
      form.dataset.pairHandled = 'true';
      addCompanion(activeAddon).finally(() => setTimeout(() => delete form.dataset.pairHandled, 500));
    });
  }

  refreshTotals(section);
  // The tested-price map loads async — re-price once it lands (covers the
  // component-initialised-first case), whichever order the two arrive in.
  onTestedPrices(() => refreshTotals(section));

  // The native picker only morphs itself on a variant change; every other
  // Horizon component listens for the event and re-renders its own slice.
  // Ours: refetch the page at the new ?variant URL and morph the
  // variant-priced Lettoria pieces (coupon, facts/scarcity, mobile head).
  // The variant id comes straight from the changed input — the picker updates
  // window.location asynchronously, so reading it here can race a step behind.
  // Rapid switching spawns overlapping fetches whose responses land out of
  // order, each morphing the page back to ITS variant — abort the in-flight
  // one and drop any response that no longer matches the latest selection.
  let variantRefreshController = null;
  section.querySelector('.variant-picker__form')?.addEventListener('change', async (event) => {
    const variantId = event.target instanceof HTMLElement ? event.target.dataset.variantId : null;
    // The ship-date calendar is pre-rendered per variant as hidden panes —
    // swap them right at the click so the box never sits a server round-trip
    // behind the picker; the `.lpo-wrap` morph below reconciles afterwards.
    if (variantId) {
      for (const pane of section.querySelectorAll('.lpo-wrap [data-po-variant]')) {
        pane.hidden = pane.dataset.poVariant !== variantId;
      }
    }
    // Re-price the button/banner/sticky from the maps right now — everything
    // refreshTotals reads (checked input, price + pre-order maps) is already
    // current at click time; the post-morph call below is the re-assert.
    refreshTotals(section);
    variantRefreshController?.abort();
    const controller = new AbortController();
    variantRefreshController = controller;
    try {
      const url = new URL(window.location.href);
      if (variantId) url.searchParams.set('variant', variantId);
      const response = await fetch(url.toString(), { signal: controller.signal });
      const text = await response.text();
      // A newer selection superseded this response while it was in flight.
      if (controller !== variantRefreshController) return;
      const html = new DOMParser().parseFromString(text, 'text/html');
      // .famp--*: the family pickers pin each size/interior row to the variant
      // matching the CURRENT material — a material change must re-render them
      // or the next size/interior swap would land on the old binding.
      for (const selector of ['lettoria-coupon', '.label__facts', '.mhead', '.label__notes', '.lpo-wrap', '.stage__po-badge', '.famp--size', '.famp--interior']) {
        const current = section.querySelector(selector);
        const next = html.querySelector(selector);
        if (current && next) morph(current, next);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('variant refresh failed', error);
    }
    refreshTotals(section);
  });

  // Horizon's product-form and sticky bar re-render their price text from the
  // picker's fetch once its ProductSelectEvent promise resolves, which lands
  // after the refresh above and wipes the injected with-code amounts. Re-assert
  // them once those listeners have finished their synchronous morph work.
  section.addEventListener(StandardEvents.productSelect, (event) => {
    event.promise?.then(() => setTimeout(() => refreshTotals(section), 0)).catch(() => {});
  });
}

// Whether the currently selected variant can be bought. The checked picker
// input carries data-option-available synchronously at click time (the
// variant JSON script inside the form only updates after the native fetch
// morphs it, so it would lag a step); single-binding products render the
// button state server-side and it never changes.
function selectedVariantAvailable(section) {
  // :not(.famp) — the family picker (size/interior product swap) is also a
  // variant-picker and renders BEFORE the binding picker; price/availability
  // must always read the material picker's checked input.
  const picker = section.querySelector('variant-picker:not(.famp)');
  if (picker) {
    const checked = picker.querySelector('input:checked');
    if (checked?.dataset.optionAvailable) return checked.dataset.optionAvailable !== 'false';
    try {
      const json = picker.querySelector('script[type="application/json"]');
      if (json) return JSON.parse(json.textContent)?.available !== false;
    } catch {
      /* fall through to available */
    }
    return true;
  }
  const button = section.querySelector('product-form-component button[name="add"]');
  return !button?.disabled;
}

// Per-variant pre-order map emitted by buy-buttons.liquid — only armed
// variants appear; absence means the variant sells normally. Server-rendered,
// covers every variant, so one parse never goes stale across morphs.
function preorderData(section) {
  const script = section.querySelector('script[data-lettoria-preorder]');
  if (!script) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

// Per-variant price map ({vid: {was, compare, now}} in cents), also from
// buy-buttons.liquid — lets refreshTotals re-price the button, WAS/TODAY
// strip and sticky bar at click time instead of parsing the banner the
// variant round-trip hasn't morphed yet. Only `was` (catalog) and
// `compare` are read; the campaign figure is recomputed from the tested
// catalog price so it can never be derived from the control price.
function variantPrices(section) {
  const script = section.querySelector('script[data-lettoria-prices]');
  if (!script) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

// Same sync-at-click-time reasoning as selectedVariantAvailable: the checked
// picker input carries data-variant-id before the native fetch lands; the
// form's hidden id input is the post-morph fallback.
function selectedVariantId(section) {
  const checked = section.querySelector('variant-picker:not(.famp) input:checked[data-variant-id]');
  if (checked) return checked.dataset.variantId;
  return section.querySelector('product-form-component input[name="id"]')?.value ?? null;
}

// Companion add-on price: campaign maths over the companion's tested catalog
// price (data-* rendered by lettoria-pair-addon.liquid).
function addonPrice(addon) {
  const catalog = parseInt(addon.dataset.catalog ?? '', 10);
  if (!Number.isFinite(catalog)) return 0;
  const tested = testedPrice(addon.dataset.variantId, catalog);
  return campaignPrice(tested, parseInt(addon.dataset.pct, 10) || 0, parseInt(addon.dataset.fixed, 10) || 0);
}

// The promise snapshot (_preorder/_preorder_batch/_preorder_eta) is
// server-rendered inside the form, but the picker's fetch morphs the form and
// hidden-input value preservation there isn't guaranteed — re-assert from the
// map on every refresh so the submitted form always matches the visible
// variant. `_preorder` = "true" is the app-contract flag Klaviyo keys on.
function syncPreorderInputs(section, po) {
  const form = section.querySelector('product-form-component form');
  if (!form) return;
  applyPreorderInputs(form, propsFromMapEntry(po));
}

function refreshTotals(section) {
  const nowEl = section.querySelector('.pban__now');
  const wasEl = section.querySelector('.pban__old');
  const addon = section.querySelector('[ref-pair-addon]:not(.is-off)');
  if (!nowEl) return;

  // Sold-out binding: the stage takes the out-of-print treatment and the
  // button says so instead of quoting a price it can't sell at. The native
  // form has already disabled the button; this re-assert must not paint
  // "Add to Cart — $X" back over Horizon's sold-out label (s24 regression).
  const available = selectedVariantAvailable(section);
  section.querySelector('lettoria-theater')?.classList.toggle('is-oop', !available);

  // Armed pre-order variant: swap the buy verb and keep the promise
  // snapshot in the form. Runs before the price-parse bailout — properties
  // must track the variant even when the banner isn't parseable.
  const po = available ? (preorderData(section)?.[selectedVariantId(section)] ?? null) : null;
  syncPreorderInputs(section, po);

  const stickyLabel = document.querySelector('.sticky-add-to-cart__button .add-to-cart-text__content > span');
  if (stickyLabel && available) {
    stickyLabel.textContent = po ? 'Pre-order' : (stickyLabel.dataset.baseLabel ?? stickyLabel.textContent);
  }

  // Second deck of the buy button ("Ships to you …") follows the variant.
  const mainBtn = section.querySelector('product-form-component button[name="add"]');
  if (mainBtn) {
    mainBtn.classList.toggle('atc--preorder', !!po);
    const btnSub = mainBtn.querySelector('[data-po-sub]');
    if (btnSub) {
      btnSub.textContent = po?.sub ?? '';
      btnSub.hidden = !po;
    }
  }

  // Catalog + compare-at for the selected variant: the per-variant map when
  // it exists (multi-binding — the coupon's data-* still describe the variant
  // the last server round-trip rendered), else the coupon's own data-*.
  // The campaign percent/fixed pair is per product, so it always comes from
  // the coupon. Everything downstream is computed from the TESTED catalog
  // price; the strip, button and sticky bar get the same figures so all
  // three read as one.
  const coupon = section.querySelector('lettoria-coupon');
  const variantId = selectedVariantId(section);
  const entry = variantPrices(section)?.[variantId] ?? null;
  const couponInt = (key) => {
    const n = parseInt(coupon?.dataset[key] ?? '', 10);
    return Number.isFinite(n) ? n : null;
  };
  const catalogCents = entry ? entry.was : couponInt('catalog');
  if (!Number.isFinite(catalogCents)) return;
  const compareCents = (entry ? entry.compare : couponInt('compare')) ?? 0;
  const percent = couponInt('pct') ?? 0;
  const fixed = couponInt('fixed') ?? 0;
  const campaignOn = percent > 0 || fixed > 0;

  const catalog = testedPrice(variantId, catalogCents);
  const baseCents = campaignPrice(catalog, percent, fixed);
  nowEl.textContent = money(baseCents);
  if (wasEl) {
    // Campaign strip strikes the catalog price; the plain sale strip strikes
    // the compare-at (left as-is when this variant has none — the morph
    // that follows removes the row).
    if (campaignOn) wasEl.textContent = money(catalog);
    else if (compareCents > catalog) wasEl.textContent = money(compareCents);
  }
  const saveBadge = section.querySelector('[ref-save-badge]');
  if (saveBadge && !campaignOn && compareCents > catalog) {
    saveBadge.textContent = `Save ${Math.round(((compareCents - catalog) * 100) / compareCents)}%`;
  }
  const addonCents = addon ? addonPrice(addon) : 0;

  const atcText = Array.from(section.querySelectorAll('.add-to-cart-text__content > span')).find(
    (el) => !el.closest('sticky-add-to-cart')
  );
  if (atcText) {
    atcText.textContent = available ? `${po ? 'Pre-order' : 'Add to Cart'} — ${money(baseCents + addonCents)}` : 'Out of print';
  }

  // Sticky bar hides itself when the variant is unavailable
  // (sticky-add-to-cart[data-variant-available]) — only touch it when live.
  const stickyPrice = document.querySelector('.sticky-add-to-cart__price');
  if (available && stickyPrice) {
    stickyPrice.innerHTML = wasEl ? `<s>${wasEl.textContent}</s> ${nowEl.textContent}` : nowEl.textContent;
  }

  // Server-rendered price nodes in this section (mobile head, binding rows,
  // add-on) come back at catalog prices after every morph — re-price them.
  repriceTested(section);
}

for (const section of document.querySelectorAll('.shopify-section')) {
  if (section.querySelector('lettoria-theater')) initLabel(section);
}
